import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { userEntity } from '@/config/entities/users';
import { GenericCRUDService } from '@/services/genericCRUD';
import { rbacRolesCRUDService } from '@/services/rbacCRUD';
import api from '@/services/api';
import { toast } from 'react-hot-toast';
import { CurrencyDollarIcon, KeyIcon, LockClosedIcon, PlusIcon, ShieldCheckIcon, UserMinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { userPositionService } from '@/services/userPositionService';
import { businessPositionService, type BusinessPosition } from '@/services/businessPositionService';

const CLEARANCE_OPTIONS = ['Public', 'Internal', 'Confidential', 'Secret', 'Top Secret'] as const;

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  tenant_id?: string;
  tenant_name: string;
  is_tenant_admin: boolean;
  created_at: string;
  clearance?: string;
  /** From list API (camelCase) */
  firstName?: string;
  lastName?: string;
  isChiefAccessOfficer?: boolean;
  isTenantAdmin?: boolean;
}

interface Role {
  id: string;
  role_name: string;
  description: string;
  status: string;
}

interface UserRoleAssignment {
  id: string;
  user_id: string;
  user_role_id: string;
  role_name: string;
  assigned_at: string;
  status: string;
}

interface RoleAssignmentForm {
  [roleId: string]: boolean;
}

interface HierarchyNode {
  id: string;
  name: string;
  level: number;
  path: string;
  parent_id?: string;
}

interface HierarchyAssignment {
  business_hierarchy_id?: string;
  functional_hierarchy_id?: string;
  geographic_hierarchy_id?: string;
}

interface PlanOption {
  id: string;
  name: string;
  billingPeriod: string;
  priceUsd: number;
  currency: string;
}

interface UserBillingSummary {
  tenantType: string;
  billingMode: 'CENTRAL' | 'DECENTRALIZED';
  activeOrPendingSubscription: {
    id: string;
    status: string;
    owner_user_id?: string | null;
    period_start?: string;
    period_end?: string;
    billing_plan_id?: string;
    plan_name?: string;
    billing_period?: string;
    price_usd?: number;
    currency?: string;
  } | null;
  pendingPlanAttempt: {
    id: string;
    billing_plan_id: string;
    created_at: string;
  } | null;
}

const UserManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRoleAssignmentModal, setShowRoleAssignmentModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [userRoleAssignments, setUserRoleAssignments] = useState<UserRoleAssignment[]>([]);
  const [roleAssignmentForm, setRoleAssignmentForm] = useState<RoleAssignmentForm>({});
  const [businessHierarchy, setBusinessHierarchy] = useState<HierarchyNode[]>([]);
  const [functionalHierarchy, setFunctionalHierarchy] = useState<HierarchyNode[]>([]);
  const [geographicHierarchy, setGeographicHierarchy] = useState<HierarchyNode[]>([]);
  const [hierarchyAssignment, setHierarchyAssignment] = useState<HierarchyAssignment>({});

  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [loadingCao, setLoadingCao] = useState(false);
  const [showAddCaoModal, setShowAddCaoModal] = useState(false);
  const [isAssigningCao, setIsAssigningCao] = useState(false);
  const [isRevokingCao, setIsRevokingCao] = useState(false);
  const [editFormPositionIds, setEditFormPositionIds] = useState<string[]>([]);
  const [editFormPrimaryPositionId, setEditFormPrimaryPositionId] = useState<string | undefined>(undefined);
  const [allPositions, setAllPositions] = useState<BusinessPosition[]>([]);
  const [positionSearchTerm, setPositionSearchTerm] = useState('');
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const [businessHierarchySearch, setBusinessHierarchySearch] = useState('');
  const [functionalHierarchySearch, setFunctionalHierarchySearch] = useState('');
  const [geographicHierarchySearch, setGeographicHierarchySearch] = useState('');
  const [businessHierarchyDropdownOpen, setBusinessHierarchyDropdownOpen] = useState(false);
  const [functionalHierarchyDropdownOpen, setFunctionalHierarchyDropdownOpen] = useState(false);
  const [geographicHierarchyDropdownOpen, setGeographicHierarchyDropdownOpen] = useState(false);
  const [editAvailablePlans, setEditAvailablePlans] = useState<PlanOption[]>([]);
  const [editFormBillingPlanId, setEditFormBillingPlanId] = useState('');
  const [editBillingSummary, setEditBillingSummary] = useState<UserBillingSummary | null>(null);
  const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);
  const [planUser, setPlanUser] = useState<User | null>(null);
  const [availablePlans, setAvailablePlans] = useState<PlanOption[]>([]);
  const [selectedBillingPlanId, setSelectedBillingPlanId] = useState('');
  const [isAssigningPlan, setIsAssigningPlan] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [billingMode, setBillingMode] = useState<'CENTRAL' | 'DECENTRALIZED'>('CENTRAL');
  const [billingReady, setBillingReady] = useState<boolean>(true);
  const positionDropdownRef = useRef<HTMLDivElement>(null);
  const businessHierarchyRef = useRef<HTMLDivElement>(null);
  const functionalHierarchyRef = useRef<HTMLDivElement>(null);
  const geographicHierarchyRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);

  const crudService = new GenericCRUDService(userEntity);

  const loadTenantUsers = async () => {
    setLoadingCao(true);
    try {
      const res = await api.get<{ success: boolean; data: User[] }>('/users/admin/users');
      const list = res.data?.data ?? [];
      setTenantUsers(list.map((u: any) => ({
        ...u,
        first_name: u.first_name ?? u.firstName ?? '',
        last_name: u.last_name ?? u.lastName ?? '',
      })));
    } catch {
      setTenantUsers([]);
    } finally {
      setLoadingCao(false);
    }
  };

  useEffect(() => {
    if (!showEditForm) {
      loadTenantUsers();
    }
  }, [showEditForm]);

  const caoList = tenantUsers.filter((u) => u.isChiefAccessOfficer);
  const nonCaoUsers = tenantUsers.filter(
    (u) => !u.isChiefAccessOfficer && !(u.isTenantAdmin ?? u.is_tenant_admin)
  );
  const canRemoveLastCao = caoList.length > 1;

  const handleAssignCao = async (user: User) => {
    setIsAssigningCao(true);
    try {
      await api.post('/users/admin/chief-access-officer/assign', { userId: user.id });
      toast.success('Chief Access Officer assigned with Top Secret clearance');
      setShowAddCaoModal(false);
      await loadTenantUsers();
      if (tableRef.current) tableRef.current.loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to assign Chief Access Officer';
      toast.error(msg);
    } finally {
      setIsAssigningCao(false);
    }
  };

  const handleRevokeCao = async (user: User) => {
    if (!canRemoveLastCao) return;
    setIsRevokingCao(true);
    try {
      await api.post('/users/admin/chief-access-officer/revoke', { userId: user.id });
      toast.success('Chief Access Officer revoked');
      await loadTenantUsers();
      if (tableRef.current) tableRef.current.loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to revoke Chief Access Officer';
      toast.error(msg);
    } finally {
      setIsRevokingCao(false);
    }
  };

  const normalizePlanOptions = (plans: any[]): PlanOption[] => {
    return (plans || []).map((plan: any) => ({
      id: plan.id,
      name: plan.name,
      billingPeriod: plan.billingPeriod ?? plan.billing_period ?? 'monthly',
      priceUsd: Number(plan.priceUsd ?? plan.price_usd ?? 0),
      currency: plan.currency ?? 'usd',
    }));
  };

  const loadAssignablePlans = async (): Promise<PlanOption[]> => {
    const isPlatformAdmin = currentUser?.role === 'Platform Administrator';

    if (isPlatformAdmin) {
      try {
        const adminPlansResponse = await api.get('/ai/admin/billing/plans');
        const adminPlans = (adminPlansResponse.data?.plans || []).filter((plan: any) => {
          const isActive = plan.isActive ?? plan.is_active ?? true;
          const isRetired = plan.isRetired ?? plan.is_retired ?? false;
          return isActive && !isRetired;
        });
        return normalizePlanOptions(adminPlans);
      } catch (error) {
        console.warn('Falling back to tenant plans for assignable plans:', error);
      }
    }

    const tenantPlansResponse = await api.get('/ai/billing/tenant/plans');
    return normalizePlanOptions(tenantPlansResponse.data?.plans || []);
  };

  const loadTenantBillingStatus = async () => {
    try {
      const response = await api.get('/ai/billing/tenant/status');
      const mode = response.data?.billingMode === 'DECENTRALIZED' ? 'DECENTRALIZED' : 'CENTRAL';
      setBillingMode(mode);
      setBillingReady(response.data?.billingReady === true);
    } catch (error) {
      console.error('Error loading tenant billing status:', error);
      // Default to CENTRAL but not ready if status cannot be loaded
      setBillingMode('CENTRAL');
      setBillingReady(false);
    }
  };

  useEffect(() => {
    loadTenantBillingStatus().catch(() => void 0);
  }, []);

  // Close position dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (positionDropdownRef.current && !positionDropdownRef.current.contains(target)) {
        setPositionDropdownOpen(false);
      }
      if (businessHierarchyRef.current && !businessHierarchyRef.current.contains(target)) {
        setBusinessHierarchyDropdownOpen(false);
      }
      if (functionalHierarchyRef.current && !functionalHierarchyRef.current.contains(target)) {
        setFunctionalHierarchyDropdownOpen(false);
      }
      if (geographicHierarchyRef.current && !geographicHierarchyRef.current.contains(target)) {
        setGeographicHierarchyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle edit user
  const handleEditUser = async (user: User) => {
    setIsCreateMode(false);
    setEditingUser(user);
    // Use user's tenant_id, or fallback to current user's tenant_id if not available
    const targetTenantId = user.tenant_id || currentUser?.tenantId;
    console.log('[UserManagement] Editing user:', { 
      userId: user.id, 
      email: user.email, 
      userTenantId: user.tenant_id,
      currentUserTenantId: currentUser?.tenantId,
      targetTenantId 
    });
    
    if (!targetTenantId) {
      console.warn('[UserManagement] No tenant_id available for user or current user');
      toast.error('Cannot load hierarchies: tenant information is missing');
      return;
    }
    
    await loadHierarchies(targetTenantId);
    await loadUserHierarchyAssignments(user.id);
    try {
      const [userPositions, positionsList, plans, billingSummaryResponse] = await Promise.all([
        userPositionService.getUserPositions(user.id),
        businessPositionService.getActivePositions(),
        loadAssignablePlans().catch(() => []),
        api.get(`/users/admin/users/${user.id}/billing-summary`).catch(() => ({ data: { data: null } })),
      ]);
      setAllPositions(positionsList);
      setEditFormPositionIds(userPositions.map(p => p.position_id));
      const primary = userPositions.find(p => p.is_primary);
      setEditFormPrimaryPositionId(primary?.position_id);
      setEditAvailablePlans(plans);
      setEditFormBillingPlanId('');
      setEditBillingSummary(billingSummaryResponse.data?.data || null);
    } catch {
      setEditFormPositionIds([]);
      setEditFormPrimaryPositionId(undefined);
      setAllPositions([]);
      setEditAvailablePlans([]);
      setEditFormBillingPlanId('');
      setEditBillingSummary(null);
    }
    setPositionSearchTerm('');
    setPositionDropdownOpen(false);
    setShowEditForm(true);
  };

  // Handle form submit
  const handleFormSubmit = async (data: any) => {
    try {
      if (isCreateMode) {
        const selectedRoleIds = Object.keys(roleAssignmentForm).filter(
          (roleId) => roleAssignmentForm[roleId]
        );
        if (selectedRoleIds.length === 0) {
          toast.error(
            t('userManagement.createRequiresRole') ||
              'Select at least one role before creating the user.'
          );
          return;
        }

        const businessHierarchyId =
          hierarchyAssignment.business_hierarchy_id ||
          (data.business_hierarchy_id as string) ||
          undefined;
        const functionalHierarchyId =
          hierarchyAssignment.functional_hierarchy_id ||
          (data.functional_hierarchy_id as string) ||
          undefined;
        const geographicHierarchyId =
          hierarchyAssignment.geographic_hierarchy_id ||
          (data.geographic_hierarchy_id as string) ||
          undefined;
        const hasHierarchy =
          Boolean(businessHierarchyId) ||
          Boolean(functionalHierarchyId) ||
          Boolean(geographicHierarchyId);
        if (!hasHierarchy) {
          toast.error(
            t('userManagement.createRequiresHierarchy') ||
              'Assign at least one hierarchy (business, functional, or geographic) before creating the user.'
          );
          return;
        }

        const targetTenantId =
          editingUser?.tenant_id || currentUser?.tenantId || undefined;
        if (!targetTenantId) {
          toast.error(
            t('userManagement.createRequiresTenant') ||
              'Tenant context is missing. Switch to a tenant or select one before creating a user.'
          );
          return;
        }

        // Backend create requires role_id + ≥1 hierarchy (+ tenant_id for platform admins)
        const createdResponse = await crudService.create({
          email: editingUser?.email || data.email,
          first_name: editingUser?.first_name || data.first_name,
          last_name: editingUser?.last_name || data.last_name,
          status: editingUser?.status || data.status || 'ACTIVE',
          tenant_id: targetTenantId,
          role_id: selectedRoleIds[0],
          business_hierarchy_id: businessHierarchyId,
          functional_hierarchy_id: functionalHierarchyId,
          geographic_hierarchy_id: geographicHierarchyId,
        });
        const createdUser = createdResponse?.data || createdResponse;

        if (!createdUser?.id) {
          throw new Error('User created but no user id returned');
        }

        // Provisioning covers extra roles, positions, billing (create already assigned primary role + hierarchy)
        const provisioningResponse = await api.put(`/users/admin/users/${createdUser.id}/provisioning`, {
          ...data,
          roleIds: selectedRoleIds,
          positionIds: editFormPositionIds,
          primaryPositionId: editFormPrimaryPositionId || null,
          billingPlanId: editFormBillingPlanId || null,
          business_hierarchy_id: businessHierarchyId,
          functional_hierarchy_id: functionalHierarchyId,
          geographic_hierarchy_id: geographicHierarchyId,
        });

        const warning = provisioningResponse?.data?.data?.warning;
        const invite = createdResponse?.invite;
        if (warning) {
          toast.success('User created. Plan assignment needs attention.');
          toast.error(warning);
        } else if (invite?.emailSent) {
          toast.success(
            `User created. Invitation with login credentials sent to ${createdUser.email || 'the user'}.`
          );
        } else if (invite?.loginUrl) {
          toast.success(
            'User created. Invitation email was not sent — activation link copied to clipboard.'
          );
          console.info('Admin invite login URL (email not sent):', invite.loginUrl);
          try {
            await navigator.clipboard.writeText(invite.loginUrl);
            toast.success('Activation link copied to clipboard.');
          } catch {
            toast.error(`Share this activation link: ${invite.loginUrl}`);
          }
        } else {
          toast.success(
            'User created successfully. Use Reset Password to resend an invitation link.'
          );
        }

        await handleEditUser(createdUser as User);
        if (tableRef.current) {
          tableRef.current.loadData();
        }
        return;
      }

      if (editingUser) {
        await api.put(`/users/admin/users/${editingUser.id}`, {
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          status: editingUser.status,
          clearance: editingUser.clearance ?? 'Internal',
        });

        const roleIds = Object.keys(roleAssignmentForm).length > 0
          ? Object.keys(roleAssignmentForm).filter((roleId) => roleAssignmentForm[roleId])
          : undefined;

        const response = await api.put(`/users/admin/users/${editingUser.id}/provisioning`, {
          ...data,
          roleIds,
          positionIds: editFormPositionIds,
          primaryPositionId: editFormPrimaryPositionId || null,
          billingPlanId: editFormBillingPlanId || null,
        });

        const warning = response?.data?.data?.warning;
        if (warning) {
          toast.success('User updated. Plan assignment needs attention.');
          toast.error(warning);
        } else {
          toast.success('User provisioning updated successfully');
        }

        await loadUserHierarchyAssignments(editingUser.id);
        try {
          const positions = await userPositionService.getUserPositions(editingUser.id);
          setEditFormPositionIds(positions.map(p => p.position_id));
          const primary = positions.find(p => p.is_primary);
          setEditFormPrimaryPositionId(primary?.position_id);
        } catch {
          // ignore
        }
        try {
          const reload = await api.get(`/users/admin/users/${editingUser.id}`);
          const updatedUser = reload.data.data || reload.data;
          setEditingUser(updatedUser);
        } catch (error) {
          console.error('Error reloading user data:', error);
        }
        try {
          const billingSummaryResponse = await api.get(`/users/admin/users/${editingUser.id}/billing-summary`);
          setEditBillingSummary(billingSummaryResponse.data?.data || null);
        } catch {
          // ignore billing summary refresh failures
        }
      }
      // Don't close the form automatically - let user decide when to close
      // setShowEditForm(false);
      // setEditingUser(null);
      // Refresh the table
      if (tableRef.current) {
        tableRef.current.loadData();
      }
    } catch (error: any) {
      console.error('Unexpected error in form submission:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unexpected error occurred';
      toast.error(`Unexpected error: ${errorMessage}`);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setIsCreateMode(false);
    setShowEditForm(false);
    setEditingUser(null);
    setEditFormPositionIds([]);
    setEditFormPrimaryPositionId(undefined);
    setAllPositions([]);
    setEditAvailablePlans([]);
    setEditFormBillingPlanId('');
    setEditBillingSummary(null);
    setPositionSearchTerm('');
    setPositionDropdownOpen(false);
  };

  const handleCreateUser = async () => {
    const tenantId = currentUser?.tenantId;
    if (tenantId) {
      await loadHierarchies(tenantId);
    } else {
      setBusinessHierarchy([]);
      setFunctionalHierarchy([]);
      setGeographicHierarchy([]);
      toast.error('Cannot load hierarchies: tenant information is missing');
    }

    try {
      const [positionsList, plans] = await Promise.all([
        businessPositionService.getActivePositions(),
        loadAssignablePlans().catch(() => []),
      ]);
      setAllPositions(positionsList);
      setEditAvailablePlans(plans);
    } catch {
      setAllPositions([]);
      setEditAvailablePlans([]);
    }

    await loadAvailableRoles();

    setIsCreateMode(true);
    setHierarchyAssignment({});
    setRoleAssignmentForm({});
    setEditFormPositionIds([]);
    setEditFormPrimaryPositionId(undefined);
    setEditFormBillingPlanId('');
    setEditBillingSummary(null);
    setPositionSearchTerm('');
    setPositionDropdownOpen(false);
    setEditingUser({
      id: '',
      email: '',
      first_name: '',
      last_name: '',
      status: 'ACTIVE',
      tenant_id: currentUser?.tenantId,
      tenant_name: currentUser?.tenantName || '',
      is_tenant_admin: false,
      created_at: new Date().toISOString(),
    });
    setShowEditForm(true);
  };

  // Position helpers for edit form
  const getPositionById = (id: string) => allPositions.find(p => p.id === id);
  const filteredPositionOptions = positionSearchTerm.trim() === ''
    ? allPositions.filter(p => !editFormPositionIds.includes(p.id))
    : allPositions.filter(p =>
        !editFormPositionIds.includes(p.id) &&
        (p.position_name.toLowerCase().includes(positionSearchTerm.toLowerCase()) ||
          (p.position_code && p.position_code.toLowerCase().includes(positionSearchTerm.toLowerCase())))
      );

  // Load available roles for assignment
  const loadAvailableRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const roles = await rbacRolesCRUDService.getAll();
      // Transform DataItem to Role format
      const transformedRoles: Role[] = roles.map((role: any) => ({
        id: role.id,
        role_name: role.role || role.name,
        description: role.description || '',
        status: role.isActive ? 'ACTIVE' : 'INACTIVE'
      }));
      setAvailableRoles(transformedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Failed to load available roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Load hierarchy data
  // If targetTenantId is provided, load hierarchies for that tenant (for platform admins)
  // Otherwise, backend will use current user's tenant_id
  const loadHierarchies = async (targetTenantId?: string) => {
    try {
      // Build query parameter if targetTenantId is provided
      const tenantParam = targetTenantId ? `?tenant_id=${targetTenantId}` : '';
      
      console.log('[UserManagement] Loading hierarchies with tenantParam:', tenantParam, 'targetTenantId:', targetTenantId);
      
      // Load business hierarchy
      const businessResponse = await api.get(`/hierarchy/business${tenantParam}`);
      console.log('[UserManagement] Business hierarchy response:', businessResponse.data);
      const businessData = businessResponse.data?.data || businessResponse.data || [];
      // API returns flat array - convert to HierarchyNode for dropdown (level/path from item or defaults)
      const businessOptions: HierarchyNode[] = Array.isArray(businessData)
        ? businessData.map(item => ({
            id: item.id,
            name: item.name || 'Unnamed',
            level: item.level ?? 0,
            path: item.path ?? item.name ?? ''
          }))
        : [];
      setBusinessHierarchy(businessOptions);

      // Load functional hierarchy
      const functionalResponse = await api.get(`/hierarchy/functional${tenantParam}`);
      console.log('[UserManagement] Functional hierarchy response:', functionalResponse.data);
      const functionalData = functionalResponse.data?.data || functionalResponse.data || [];
      const functionalOptions: HierarchyNode[] = Array.isArray(functionalData)
        ? functionalData.map(item => ({
            id: item.id,
            name: item.name || 'Unnamed',
            level: item.level ?? 0,
            path: item.path ?? item.name ?? ''
          }))
        : [];
      setFunctionalHierarchy(functionalOptions);

      // Load geographic hierarchy
      const geographicResponse = await api.get(`/hierarchy/geographic${tenantParam}`);
      console.log('[UserManagement] Geographic hierarchy response:', geographicResponse.data);
      const geographicData = geographicResponse.data?.data || geographicResponse.data || [];
      const geographicOptions: HierarchyNode[] = Array.isArray(geographicData)
        ? geographicData.map(item => ({
            id: item.id,
            name: item.name || 'Unnamed',
            level: item.level ?? 0,
            path: item.path ?? item.name ?? ''
          }))
        : [];
      setGeographicHierarchy(geographicOptions);
      
      console.log('[UserManagement] Loaded hierarchies:', {
        business: businessData.length,
        functional: functionalData.length,
        geographic: geographicData.length
      });
      
    } catch (error: any) {
      console.error('[UserManagement] Error loading hierarchies:', error);
      console.error('[UserManagement] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Failed to load hierarchy data: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    }
  };

  // Load user's current hierarchy assignments
  const loadUserHierarchyAssignments = async (userId: string) => {
    try {
      const response = await api.get(`/users/hierarchy/assignments/${userId}`);
      const assignments = response.data.data || {};
      setHierarchyAssignment({
        business_hierarchy_id: assignments.business_hierarchy_id,
        functional_hierarchy_id: assignments.functional_hierarchy_id,
        geographic_hierarchy_id: assignments.geographic_hierarchy_id
      });
    } catch (error) {
      console.error('Error loading user hierarchy assignments:', error);
      setHierarchyAssignment({});
    }
  };

  // Load user's current role assignments
  const loadUserRoleAssignments = async (userId: string) => {
    try {
            const response = await api.get(`/platform/user-role-assignments?user_id=${userId}`);
            
      const assignments = response.data.data || [];
      setUserRoleAssignments(assignments);
      
      // Initialize the form with current assignments (only ACTIVE roles are checked)
      const formData: RoleAssignmentForm = {};
      availableRoles.forEach(role => {
        formData[role.id] = assignments.some((assignment: any) => 
          assignment.role_id === role.id && assignment.status === 'ACTIVE'
        );
      });
      setRoleAssignmentForm(formData);
    } catch (error) {
      console.error('Error loading user role assignments:', error);
      setUserRoleAssignments([]);
      setRoleAssignmentForm({});
    }
  };

  // Update form when available roles change
  useEffect(() => {
    if (availableRoles.length > 0 && userRoleAssignments.length > 0) {
      const formData: RoleAssignmentForm = {};
      availableRoles.forEach(role => {
        formData[role.id] = userRoleAssignments.some((assignment: any) => 
          assignment.role_id === role.id && assignment.status === 'ACTIVE'
        );
      });
      setRoleAssignmentForm(formData);
    }
  }, [availableRoles, userRoleAssignments]);

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleRoleAssignment = async (user: User) => {
    setSelectedUser(user);
    await loadAvailableRoles();
    await loadUserRoleAssignments(user.id);
    setShowRoleAssignmentModal(true);
  };

  const handleOpenAssignPlanModal = async (user: User) => {
    if (billingMode === 'CENTRAL' && !billingReady) {
      toast.error('Centralized billing is not configured. Please add a payment method in the subscription screen before assigning plans.');
      return;
    }

    setPlanUser(user);
    setShowAssignPlanModal(true);
    setSelectedBillingPlanId('');
    setIsLoadingPlans(true);
    try {
      const plans = await loadAssignablePlans();
      setAvailablePlans(plans);
      if (plans.length > 0) {
        setSelectedBillingPlanId(plans[0].id);
      } else {
        toast.error('No active plans are available for this tenant');
      }
    } catch (error: any) {
      console.error('Error loading plans for assignment:', error);
      toast.error(error?.response?.data?.error || 'Failed to load billing plans');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const closeAssignPlanModal = () => {
    setShowAssignPlanModal(false);
    setPlanUser(null);
    setAvailablePlans([]);
    setSelectedBillingPlanId('');
    setIsAssigningPlan(false);
  };

  const handleAssignPlanToUser = async () => {
    if (!planUser?.id) {
      toast.error('No user selected');
      return;
    }
    if (!selectedBillingPlanId) {
      toast.error('Please select a plan');
      return;
    }

    setIsAssigningPlan(true);
    try {
      const response = await api.post(`/ai/billing/users/${planUser.id}/assign-plan`, {
        billingPlanId: selectedBillingPlanId,
        status: 'active',
      });

      const result = response.data || {};
      const mode = result.mode;
      const appliedImmediately = result.appliedImmediately === true;
      const requiresUserCheckout = result.requiresUserCheckout === true;

      if (mode === 'DECENTRALIZED' && requiresUserCheckout) {
        toast.success('Plan intent recorded. User must complete checkout to activate.');
      } else if (appliedImmediately) {
        toast.success('Plan assigned and entitlement applied successfully.');
      } else {
        toast.success('Plan assignment saved successfully.');
      }

      closeAssignPlanModal();
      if (tableRef.current) {
        tableRef.current.loadData();
      }
    } catch (error: any) {
      console.error('Error assigning plan to user:', error);
      toast.error(error?.response?.data?.error || 'Failed to assign plan');
    } finally {
      setIsAssigningPlan(false);
    }
  };


  const handleAssignRoles = async () => {
    if (!selectedUser) {
      toast.error('No user selected');
      return;
    }

    try {
      const newAssignments = Object.keys(roleAssignmentForm).filter((roleId) => roleAssignmentForm[roleId]);

      await api.put(`/users/admin/users/${selectedUser.id}/provisioning`, {
        roleIds: newAssignments,
      });

      toast.success('Roles updated successfully');
      await loadUserRoleAssignments(selectedUser.id);
      if (tableRef.current) {
        tableRef.current.loadData();
      }
    } catch (error: any) {
      console.error('Error updating roles:', error);
      toast.error(error.response?.data?.error || 'Failed to update roles');
    }
  };


  const handleSubmitPassword = async () => {
    // Admin password reset generates a temporary login link (email + optional clipboard fallback)
    try {
      if (!selectedUser?.id) {
        toast.error('No user selected for password reset.');
        return;
      }

      const response = await api.post('/users/admin/reset-password', {
        userId: selectedUser.id
      });
      const payload = response?.data || {};
      const loginUrl = payload?.data?.loginUrl as string | undefined;
      const emailSent = payload?.emailSent !== false;

      if (emailSent) {
        toast.success(
          `Password reset link sent to ${selectedUser.email || 'user'}.`
        );
      } else if (loginUrl) {
        toast.success(
          'Login link created, but email could not be sent. Link copied to clipboard.'
        );
        try {
          await navigator.clipboard.writeText(loginUrl);
        } catch {
          toast.error(`Share this login link: ${loginUrl}`);
        }
        console.info('Admin reset login URL (email not sent):', loginUrl);
      } else {
        toast.success(payload?.message || 'Password reset completed.');
      }

      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error resetting password:', error);

      // Network / proxy down while API restarts is a common local failure mode
      if (!error?.response) {
        toast.error(
          'Could not reach the API. Wait for the backend to finish starting, then try again.'
        );
        return;
      }

      let errorMessage = 'Failed to reset password';
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          errorMessage =
            typeof errorData.error === 'string'
              ? errorData.error
              : errorData.error?.message || errorMessage;
        } else if (errorData?.message) {
          errorMessage =
            typeof errorData.message === 'string' ? errorData.message : errorMessage;
        }
      } else if (error?.message) {
        errorMessage = typeof error.message === 'string' ? error.message : errorMessage;
      }

      toast.error(errorMessage);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
  };

  const closeRoleAssignmentModal = () => {
    setShowRoleAssignmentModal(false);
    setSelectedUser(null);
    setUserRoleAssignments([]);
    setRoleAssignmentForm({});
  };


  const handleDeleteUser = async (user: User) => {
    // FlexibleCRUDTable already shows confirmation, so delete directly
    if (!user) return;

    try {
      await crudService.delete(user.id);
      // Don't show success toast here - FlexibleCRUDTable will show its own
      // The table will automatically reload after successful delete
    } catch (error: any) {
      console.error('Error deleting user:', error);
      
      // Extract error message properly - handle different error response formats
      let errorMessage = 'Failed to delete user';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        // Handle different error response formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          // Backend returns { error: { message: ..., code: ..., ... } }
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : (errorData.error?.message || errorMessage);
        } else if (errorData?.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage;
        }
      } else if (error?.message) {
        errorMessage = typeof error.message === 'string' ? error.message : errorMessage;
      }
      
      toast.error(errorMessage);
      // Re-throw error so FlexibleCRUDTable knows the delete failed
      throw error;
    }
  };


  // Custom actions for the CRUD table
  const customActions = [
    {
      label: 'Assign Plan',
      icon: CurrencyDollarIcon,
      onClick: handleOpenAssignPlanModal,
      variant: 'primary' as const
    },
    {
      label: t('userManagement.assignRoles'),
      icon: ShieldCheckIcon,
      onClick: handleRoleAssignment,
      variant: 'primary' as const
    },
    {
      label: t('userManagement.resetPassword'),
      icon: KeyIcon,
      onClick: handleResetPassword,
      variant: 'secondary' as const
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-font mb-2">{t('userManagement.title')}</h1>
          <p className="text-secondary-font">{t('userManagement.description')}</p>
        </div>

        {/* Chief Access Officer(s) - at least one per tenant; last one cannot be removed */}
        {!showEditForm && (
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-primary-font flex items-center gap-2">
                  <LockClosedIcon className="h-5 w-5 text-primary-font" />
                  Chief Access Officer(s)
                </h2>
                <Button
                  variant="primary"
                  onClick={() => setShowAddCaoModal(true)}
                  disabled={loadingCao || nonCaoUsers.length === 0}
                >
                  <PlusIcon className="h-4 w-4 mr-2 inline" />
                  Add Chief Access Officer
                </Button>
              </div>
              <p className="text-sm text-secondary-font mb-4">
                Every organization must have at least one Chief Access Officer (Top Secret). They set access levels for employees. The last CAO cannot be removed.
              </p>
              {loadingCao ? (
                <p className="text-secondary-font">Loading…</p>
              ) : caoList.length === 0 ? (
                <p className="text-secondary-font">No Chief Access Officer assigned. Add one to continue.</p>
              ) : (
                <ul className="space-y-2">
                  {caoList.map((u) => (
                    <li
                      key={u.id}
                      className="flex items-center justify-between py-2 px-3 rounded-md bg-secondary-background border border-border-color"
                    >
                      <span className="text-primary-font">
                        {(u.firstName ?? u.first_name) || ''} {(u.lastName ?? u.last_name) || ''}
                        {((u.firstName ?? u.first_name) || (u.lastName ?? u.last_name)) ? ' · ' : ''}
                        {u.email}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRevokeCao(u)}
                        disabled={!canRemoveLastCao || isRevokingCao}
                        title={!canRemoveLastCao ? 'Cannot remove the last Chief Access Officer' : 'Remove CAO'}
                      >
                        <UserMinusIcon className="h-4 w-4 mr-1 inline" />
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        )}

        {/* Users Table using CRUD Framework */}
        {!showEditForm ? (
          <Card>
            <div className="p-6">
              <FlexibleCRUDTable
                entity={userEntity}
                service={crudService}
                onCreate={handleCreateUser}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                customActions={customActions}
                searchPlaceholder={t('userManagement.searchUsers')}
                showPagination={true}
                showSearch={true}
                onOperationComplete={() => {
                  // Refresh data after operations
                }}
              />
            </div>
          </Card>
        ) : (
          /* Edit Form */
          <Card>
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isCreateMode
                    ? (t('userManagement.editForm.createTitle') || 'Create User')
                    : (t('userManagement.editForm.editTitle', {
                        firstName: editingUser?.first_name || '',
                        lastName: editingUser?.last_name || '',
                      }) ||
                        `Edit User: ${editingUser?.first_name || ''} ${editingUser?.last_name || ''}`.trim())}
                </h2>
                <p className="text-gray-600 mt-2">
                  {isCreateMode
                    ? (t('userManagement.editForm.createDescription') ||
                        'Enter user details, assign at least one role and one hierarchy, then save.')
                    : (t('userManagement.editForm.editDescription') ||
                        'Update the user details below.')}
                </p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const data = Object.fromEntries(formData.entries());
                handleFormSubmit(data);
              }}>
                <div className="space-y-6">
                  {/* Basic User Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editingUser?.email || ''}
                      onChange={(e) => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      readOnly={!isCreateMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.firstName')}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={editingUser?.first_name || ''}
                      onChange={(e) => setEditingUser(prev => prev ? {...prev, first_name: e.target.value} : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {!isCreateMode && (
                      <div className="text-xs text-gray-500 mt-1">
                        Current value: {editingUser?.first_name || 'empty'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.lastName')}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={editingUser?.last_name || ''}
                      onChange={(e) => setEditingUser(prev => prev ? {...prev, last_name: e.target.value} : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {!isCreateMode && (
                      <div className="text-xs text-gray-500 mt-1">
                        Current value: {editingUser?.last_name || 'empty'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.status')}
                    </label>
                    <select
                      name="status"
                      value={editingUser?.status || 'ACTIVE'}
                      onChange={(e) => setEditingUser(prev => prev ? {...prev, status: e.target.value} : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">{t('userManagement.statusActive')}</option>
                      <option value="INACTIVE">{t('userManagement.statusInactive')}</option>
                      <option value="PENDING">{t('userManagement.statusPending')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.clearance') || 'Clearance'}
                    </label>
                    <select
                      value={editingUser?.clearance ?? 'Internal'}
                      onChange={(e) => setEditingUser(prev => prev ? { ...prev, clearance: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isCreateMode}
                    >
                      {CLEARANCE_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {!isCreateMode && (
                      <p className="text-xs text-gray-500 mt-1">{t('userManagement.clearanceHelp') || 'Max classification this user can view. Default: Internal.'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.billingPlan.label') || 'Billing Plan (optional)'}
                    </label>
                    <select
                      value={editFormBillingPlanId}
                      onChange={(e) => setEditFormBillingPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">
                        {isCreateMode
                          ? (t('userManagement.billingPlan.noPlanOnCreate') || 'No plan on create')
                          : (t('userManagement.billingPlan.doNotChange') || 'Do not change plan')}
                      </option>
                      {editAvailablePlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {plan.billingPeriod} - {plan.currency?.toUpperCase() || 'USD'} {Number(plan.priceUsd || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Hierarchy Assignments */}
                <div className="border-t pt-6">
                  {isCreateMode && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                      {t('userManagement.createRequirementsHint') ||
                        'Required to create: at least one role and at least one hierarchy assignment.'}
                    </p>
                  )}

                  {/* Roles (required on create) */}
                  {isCreateMode && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        {t('userManagement.roles') || 'Roles'}{' '}
                        <span className="text-red-500">*</span>
                      </h3>
                      {isLoadingRoles ? (
                        <p className="text-sm text-gray-500">{t('common.loading') || 'Loading…'}</p>
                      ) : availableRoles.filter((r) => r.status === 'ACTIVE').length === 0 ? (
                        <p className="text-sm text-gray-500">
                          {t('userManagement.noRolesAvailable') || 'No roles available'}
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {availableRoles
                            .filter((role) => role.status === 'ACTIVE')
                            .map((role) => (
                              <label
                                key={role.id}
                                className={`flex items-start gap-2 p-3 border rounded-md cursor-pointer ${
                                  roleAssignmentForm[role.id]
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-white border-gray-200'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(roleAssignmentForm[role.id])}
                                  onChange={(e) =>
                                    setRoleAssignmentForm((prev) => ({
                                      ...prev,
                                      [role.id]: e.target.checked,
                                    }))
                                  }
                                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                                <span>
                                  <span className="block font-medium text-sm text-gray-900">
                                    {role.role_name}
                                  </span>
                                  {role.description ? (
                                    <span className="block text-xs text-gray-500">{role.description}</span>
                                  ) : null}
                                </span>
                              </label>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current Billing State */}
                  {!isCreateMode && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        {t('userManagement.billingSummary.title') || 'Current Billing State'}
                      </h3>
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <span className="text-gray-500">
                              {t('userManagement.billingSummary.tenantType') || 'Tenant Type:'}
                            </span>{' '}
                            <span className="font-medium">
                              {editBillingSummary?.tenantType ||
                                t('userManagement.billingSummary.unknown') ||
                                'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">
                              {t('userManagement.billingSummary.billingMode') || 'Billing Mode:'}
                            </span>{' '}
                            <span className="font-medium">
                              {editBillingSummary?.billingMode ||
                                t('userManagement.billingSummary.unknown') ||
                                'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">
                              {t('userManagement.billingSummary.currentPlan') || 'Current Plan:'}
                            </span>{' '}
                            <span className="font-medium">
                              {editBillingSummary?.activeOrPendingSubscription?.plan_name || 'None'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">
                              {t('userManagement.billingSummary.subscriptionStatus') || 'Subscription Status:'}
                            </span>{' '}
                            <span className="font-medium">
                              {editBillingSummary?.activeOrPendingSubscription?.status || 'None'}
                            </span>
                          </div>
                          <div className="md:col-span-2">
                            <span className="text-gray-500">
                              {t('userManagement.billingSummary.pendingCheckoutIntent') ||
                                'Pending Checkout Intent:'}
                            </span>{' '}
                            <span className="font-medium">
                              {editBillingSummary?.pendingPlanAttempt ? `Yes (${editBillingSummary.pendingPlanAttempt.billing_plan_id})` : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Business Hierarchy (typeahead) */}
                    <div ref={businessHierarchyRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Hierarchy
                      </label>
                      <input
                        type="text"
                        value={
                          businessHierarchySearch ||
                          businessHierarchy.find((n) => n.id === hierarchyAssignment.business_hierarchy_id)?.name ||
                          ''
                        }
                        onChange={(e) => {
                          setBusinessHierarchySearch(e.target.value);
                          setBusinessHierarchyDropdownOpen(true);
                        }}
                        onFocus={() => setBusinessHierarchyDropdownOpen(true)}
                        placeholder="Type to search business hierarchy..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="hidden"
                        name="business_hierarchy_id"
                        value={hierarchyAssignment.business_hierarchy_id || ''}
                      />
                      {businessHierarchyDropdownOpen && (
                        <div className="mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm">
                          {businessHierarchy
                            .filter((node) =>
                              businessHierarchySearch
                                ? node.name.toLowerCase().includes(businessHierarchySearch.toLowerCase())
                                : true
                            )
                            .map((node) => (
                              <button
                                key={node.id}
                                type="button"
                                onClick={() => {
                                  setHierarchyAssignment((prev) => ({
                                    ...prev,
                                    business_hierarchy_id: node.id,
                                  }));
                                  setBusinessHierarchySearch(node.name);
                                  setBusinessHierarchyDropdownOpen(false);
                                }}
                                className="flex w-full items-center px-3 py-2 text-left hover:bg-gray-50"
                              >
                                <span className="truncate">{node.name}</span>
                              </button>
                            ))}
                          {businessHierarchy.length === 0 && (
                            <div className="px-3 py-2 text-gray-500">No business hierarchies available</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Functional Hierarchy (typeahead) */}
                    <div ref={functionalHierarchyRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Functional Hierarchy
                      </label>
                      <input
                        type="text"
                        value={
                          functionalHierarchySearch ||
                          functionalHierarchy.find((n) => n.id === hierarchyAssignment.functional_hierarchy_id)?.name ||
                          ''
                        }
                        onChange={(e) => {
                          setFunctionalHierarchySearch(e.target.value);
                          setFunctionalHierarchyDropdownOpen(true);
                        }}
                        onFocus={() => setFunctionalHierarchyDropdownOpen(true)}
                        placeholder="Type to search functional hierarchy..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="hidden"
                        name="functional_hierarchy_id"
                        value={hierarchyAssignment.functional_hierarchy_id || ''}
                      />
                      {functionalHierarchyDropdownOpen && (
                        <div className="mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm">
                          {functionalHierarchy
                            .filter((node) =>
                              functionalHierarchySearch
                                ? node.name.toLowerCase().includes(functionalHierarchySearch.toLowerCase())
                                : true
                            )
                            .map((node) => (
                              <button
                                key={node.id}
                                type="button"
                                onClick={() => {
                                  setHierarchyAssignment((prev) => ({
                                    ...prev,
                                    functional_hierarchy_id: node.id,
                                  }));
                                  setFunctionalHierarchySearch(node.name);
                                  setFunctionalHierarchyDropdownOpen(false);
                                }}
                                className="flex w-full items-center px-3 py-2 text-left hover:bg-gray-50"
                              >
                                <span className="truncate">{node.name}</span>
                              </button>
                            ))}
                          {functionalHierarchy.length === 0 && (
                            <div className="px-3 py-2 text-gray-500">No functional hierarchies available</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Geographic Hierarchy (typeahead) */}
                    <div ref={geographicHierarchyRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Geographic Hierarchy
                      </label>
                      <input
                        type="text"
                        value={
                          geographicHierarchySearch ||
                          geographicHierarchy.find((n) => n.id === hierarchyAssignment.geographic_hierarchy_id)?.name ||
                          ''
                        }
                        onChange={(e) => {
                          setGeographicHierarchySearch(e.target.value);
                          setGeographicHierarchyDropdownOpen(true);
                        }}
                        onFocus={() => setGeographicHierarchyDropdownOpen(true)}
                        placeholder="Type to search geographic hierarchy..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="hidden"
                        name="geographic_hierarchy_id"
                        value={hierarchyAssignment.geographic_hierarchy_id || ''}
                      />
                      {geographicHierarchyDropdownOpen && (
                        <div className="mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm">
                          {geographicHierarchy
                            .filter((node) =>
                              geographicHierarchySearch
                                ? node.name.toLowerCase().includes(geographicHierarchySearch.toLowerCase())
                                : true
                            )
                            .map((node) => (
                              <button
                                key={node.id}
                                type="button"
                                onClick={() => {
                                  setHierarchyAssignment((prev) => ({
                                    ...prev,
                                    geographic_hierarchy_id: node.id,
                                  }));
                                  setGeographicHierarchySearch(node.name);
                                  setGeographicHierarchyDropdownOpen(false);
                                }}
                                className="flex w-full items-center px-3 py-2 text-left hover:bg-gray-50"
                              >
                                <span className="truncate">{node.name}</span>
                              </button>
                            ))}
                          {geographicHierarchy.length === 0 && (
                            <div className="px-3 py-2 text-gray-500">No geographic hierarchies available</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Position Assignments */}
                <div className="border-t pt-6" ref={positionDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('userManagement.positions') || 'Positions'}
                  </label>
                  {editFormPositionIds.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {editFormPositionIds.map((positionId) => {
                          const position = getPositionById(positionId);
                          if (!position) return null;
                          const isPrimary = editFormPrimaryPositionId === positionId;
                          return (
                            <span
                              key={positionId}
                              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setEditFormPositionIds(prev => prev.filter(id => id !== positionId));
                                  if (editFormPrimaryPositionId === positionId) {
                                    const remaining = editFormPositionIds.filter(id => id !== positionId);
                                    setEditFormPrimaryPositionId(remaining[0] || undefined);
                                  }
                                }}
                                className="text-gray-400 hover:text-red-600 p-0.5 -ml-0.5 rounded"
                                title={t('common.remove') || 'Remove'}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                              <span className="text-gray-800">{position.position_name}</span>
                              {isPrimary && (
                                <span className="text-xs text-green-700 font-medium">(Primary)</span>
                              )}
                              {!isPrimary && editFormPositionIds.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setEditFormPrimaryPositionId(positionId)}
                                  className="text-xs text-blue-600 hover:underline ml-1"
                                >
                                  {t('position.makePrimary') || 'Make primary'}
                                </button>
                              )}
                            </span>
                          );
                        })}
                    </div>
                  )}
                  {/* Type to add position */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userManagement.addPosition') || 'Add position'}
                    </label>
                    <input
                      type="text"
                      value={positionSearchTerm}
                      onChange={(e) => {
                        setPositionSearchTerm(e.target.value);
                        setPositionDropdownOpen(true);
                      }}
                      onFocus={() => setPositionDropdownOpen(true)}
                      placeholder={t('userManagement.typePositionPlaceholder') || 'Type to search and select a position...'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    {positionDropdownOpen && filteredPositionOptions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-52 overflow-auto">
                        {filteredPositionOptions.map((position) => (
                          <button
                            key={position.id}
                            type="button"
                            onClick={() => {
                              setEditFormPositionIds(prev => {
                                if (prev.includes(position.id)) return prev;
                                if (prev.length === 0) setEditFormPrimaryPositionId(position.id);
                                return [...prev, position.id];
                              });
                              setPositionSearchTerm('');
                              setPositionDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <span className="font-medium">{position.position_name}</span>
                            {position.position_code && (
                              <span className="text-gray-500 ml-2">{position.position_code}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {positionDropdownOpen && positionSearchTerm.trim() !== '' && filteredPositionOptions.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">
                        {t('userManagement.noPositionsFound') || 'No matching positions'}
                      </div>
                    )}
                  </div>
                </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button type="button" variant="outline" onClick={handleFormCancel}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit">
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* Password Reset Modal */}
        <Modal
          isOpen={showPasswordModal}
          onClose={closePasswordModal}
          title={t('passwordManagement.resetPassword')}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This will send a temporary login link to <strong>{selectedUser?.email}</strong>. 
              The user will be able to log in without a password using this link, and will then be required to set a new password.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> The login link will expire in 24 hours. The user must set a new password immediately after logging in.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={closePasswordModal}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmitPassword}>
                Send Reset Link
              </Button>
            </div>
          </div>
        </Modal>

        {/* Role Assignment Modal */}
        <Modal
          isOpen={showRoleAssignmentModal}
          onClose={closeRoleAssignmentModal}
          title={`Assign Roles to ${selectedUser?.first_name} ${selectedUser?.last_name}`}
          size="lg"
        >
          <div className="space-y-6">
            {/* Role Assignments */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Role Assignments</h3>
              {availableRoles.length === 0 ? (
                <p className="text-gray-500">No roles available</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {availableRoles
                    .filter(role => role.status === 'ACTIVE')
                    .map((role) => {
                      const assignment = userRoleAssignments.find((assignment: any) => 
                        assignment.role_id === role.id
                      );
                      const isCurrentlyAssigned = assignment && assignment.status === 'ACTIVE';
                      const isInactive = assignment && assignment.status === 'INACTIVE';
                      const isSelected = roleAssignmentForm[role.id] || false;
                      const isNewlySelected = isSelected && !isCurrentlyAssigned;
                      const isRemoved = !isSelected && isCurrentlyAssigned;
                      
                      return (
                        <div key={role.id} className={`flex items-center space-x-3 p-3 border rounded-md ${
                          isCurrentlyAssigned ? 'bg-green-50 border-green-200' : 
                          isInactive ? 'bg-gray-50 border-gray-200' :
                          isNewlySelected ? 'bg-blue-50 border-blue-200' :
                          isRemoved ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
                        }`}>
                          <input
                            type="checkbox"
                            id={`role-${role.id}`}
                            checked={isSelected}
                            onChange={(e) => setRoleAssignmentForm(prev => ({
                              ...prev,
                              [role.id]: e.target.checked
                            }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`role-${role.id}`} className="flex-1 cursor-pointer">
                            <div className="flex items-center space-x-2">
                              <div className="font-medium">{role.role_name}</div>
                              {isCurrentlyAssigned && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Currently Assigned
                                </span>
                              )}
                              {isInactive && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inactive
                                </span>
                              )}
                              {isNewlySelected && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Will Add
                                </span>
                              )}
                              {isRemoved && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Will Remove
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{role.description}</div>
                          </label>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Update Roles Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleAssignRoles}
                disabled={isLoadingRoles}
              >
                Update Roles
              </Button>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={closeRoleAssignmentModal}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Add Chief Access Officer Modal */}
        <Modal
          isOpen={showAddCaoModal}
          onClose={() => setShowAddCaoModal(false)}
          title="Add Chief Access Officer"
        >
          <div className="space-y-4">
            <p className="text-sm text-secondary-font">
              Select a user to assign as Chief Access Officer. They will receive Top Secret clearance and can set access levels for other employees. Tenant administrators cannot be Chief Access Officers.
            </p>
            {nonCaoUsers.length === 0 ? (
              <p className="text-secondary-font">No users available. A user cannot be both tenant administrator and Chief Access Officer; only non–tenant-admins who are not already CAOs are listed.</p>
            ) : (
              <ul className="max-h-64 overflow-y-auto space-y-2 border border-border-color rounded-md p-2">
                {nonCaoUsers.map((u) => (
                  <li key={u.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-secondary-background">
                    <span className="text-primary-font">
                      {(u.firstName ?? u.first_name) || ''} {(u.lastName ?? u.last_name) || ''}
                      {((u.firstName ?? u.first_name) || (u.lastName ?? u.last_name)) ? ' · ' : ''}
                      {u.email}
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAssignCao(u)}
                      disabled={isAssigningCao}
                    >
                      {isAssigningCao ? 'Assigning...' : 'Assign as CAO'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowAddCaoModal(false)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Plan Assignment Modal */}
        <Modal
          isOpen={showAssignPlanModal}
          onClose={closeAssignPlanModal}
          title={`Assign Plan to ${planUser?.first_name || ''} ${planUser?.last_name || ''}`.trim()}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a billing plan for <strong>{planUser?.email || 'selected user'}</strong>.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Plan
              </label>
              <select
                value={selectedBillingPlanId}
                onChange={(e) => setSelectedBillingPlanId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoadingPlans || isAssigningPlan}
              >
                {isLoadingPlans ? (
                  <option value="">Loading plans...</option>
                ) : availablePlans.length === 0 ? (
                  <option value="">No plans available</option>
                ) : (
                  availablePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.billingPeriod} - {plan.currency?.toUpperCase() || 'USD'} {Number(plan.priceUsd || 0).toFixed(2)}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-900">
                In CENTRAL mode, assignment applies immediately. In DECENTRALIZED mode, this creates a checkout intent for the user.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={closeAssignPlanModal} disabled={isAssigningPlan}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleAssignPlanToUser}
                disabled={isAssigningPlan || isLoadingPlans || !selectedBillingPlanId}
              >
                {isAssigningPlan ? 'Assigning...' : 'Assign Plan'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default UserManagementPage;
